import { jsx as _jsx } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { EmailValidationResponseStatus } from '@as-react/common-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import FormValidationUtil from '../../../util/FormValidationUtil';
import { validateEmail, validateEmailExists } from '../../email/EmailValidationUtil';
import { TextAlpha } from '@yonderland/andes-react';
import { FormEmailError } from '../FormEmailError';
export const EmailValidationWrapper = ({ emailFieldKey, checkExists, errorMessages, sendEmail, disableValidation = false, children, setIsCollapsedForm, defaultSkipValidationFor, }) => {
    var _a;
    const { watch, getFieldState, setValue, formState, clearErrors, setError } = useFormContext();
    const { isSubmitting } = formState;
    const emailValue = watch(emailFieldKey);
    const { error: emailError } = getFieldState(emailFieldKey, formState);
    const validatedEmail = useRef(null);
    const { t } = useTranslation(I18N_NAMESPACES.general);
    const [suggestedEmail, setSuggestedEmail] = useState();
    const [emailStatus, setEmailStatus] = useState(undefined);
    const [skipValidationFor, setSkipValidationFor] = useState(defaultSkipValidationFor);
    const skipEmailValidation = Boolean(emailValue) && Boolean(skipValidationFor) && skipValidationFor === emailValue;
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const requiredMessage = (_a = errorMessages.required) !== null && _a !== void 0 ? _a : t('email.error.required');
    const validateEmailCallback = useCallback(validateEmail(servicesEndpoint, skipEmailValidation, emailStatus, setSuggestedEmail, setEmailStatus, true, skipValidationFor), [skipEmailValidation, emailStatus, skipValidationFor]);
    const schema = Yup.string()
        .when('$regularCheck', ([regularCheck], tmpSchema) => regularCheck
        ? tmpSchema.required(requiredMessage).matches(FormValidationUtil.emailPatternRegex, errorMessages.invalidEmail)
        : tmpSchema)
        .when('$backendEmailValidationEnabled', ([backendValidation], tmpSchema) => backendValidation ? tmpSchema.test(validateEmailCallback).required(requiredMessage) : tmpSchema)
        .when('$checkExists', ([contextCheckExists], tmpSchema) => contextCheckExists
        ? tmpSchema
            .test(validateEmailExists(servicesEndpoint, defaultRequestParams, errorMessages, true, true, sendEmail, setIsCollapsedForm))
            .required(requiredMessage)
        : tmpSchema)
        .when('$checkExistsWithValidation', ([checkExistsWithValidation], tmpSchema) => checkExistsWithValidation
        ? tmpSchema
            .test(validateEmailExists(servicesEndpoint, defaultRequestParams, errorMessages, true, true, sendEmail, setIsCollapsedForm, true, skipEmailValidation, emailStatus, setSuggestedEmail, setEmailStatus))
            .required(requiredMessage)
        : tmpSchema);
    const handleEmailValidation = (shouldCheckExists) => (email) => {
        if (email === validatedEmail.current) {
            return emailError === null || emailError === void 0 ? void 0 : emailError.message;
        }
        validatedEmail.current = email;
        const backendEmailValidationEnabledContext = !(shouldCheckExists && checkExists);
        const checkExistsWithValidationContext = shouldCheckExists && checkExists;
        return schema
            .validate(email, {
            context: {
                checkExists: false,
                backendEmailValidationEnabled: backendEmailValidationEnabledContext,
                checkExistsWithValidation: checkExistsWithValidationContext,
                regularCheck: !backendEmailValidationEnabledContext && !checkExistsWithValidationContext,
            },
        })
            .then(() => {
            clearErrors(emailFieldKey);
            setEmailStatus(undefined);
            return undefined;
        })
            .catch(error => {
            setError(emailFieldKey, { type: 'validate', message: error.message });
            setEmailStatus(error.message);
            return error.message;
        });
    };
    const handleAcceptClick = () => {
        setEmailStatus(`${EmailValidationResponseStatus.VALID}_${suggestedEmail}`);
        setValue(emailFieldKey, suggestedEmail, { shouldValidate: true });
    };
    const handleIgnoreClick = () => {
        clearErrors(emailFieldKey);
    };
    const renderError = (msg, textProps) => (_jsx(TextAlpha, Object.assign({ variant: "bodyS" }, textProps, { children: _jsx(FormEmailError, { emailStatus: msg, suggestedEmail: suggestedEmail, requiredErrorLabel: requiredMessage, onAcceptClick: handleAcceptClick, onIgnoreClick: handleIgnoreClick }) })));
    useEffect(() => {
        if ((emailError === null || emailError === void 0 ? void 0 : emailError.message) && setEmailStatus) {
            setEmailStatus(emailError.message);
        }
    }, [emailError]);
    useEffect(() => {
        if (skipValidationFor && emailValue !== skipValidationFor) {
            setSkipValidationFor(undefined);
        }
    }, [emailValue]);
    return (_jsx("div", { children: children({
            validate: disableValidation ? undefined : handleEmailValidation(!isSubmitting),
            renderError,
        }) }));
};
